import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Radio, notification } from 'antd';
import Button from 'components/Button';
import {
  getPaymentPreferences,
  updatePaymentPreferences,
  getOrganizationSettings,
} from 'services/api';
import { Customer, PaymentPreference, OrganizationSetting } from './types';

interface PaymentPreferenceProps {
  onComplete: (isDigital: boolean) => void;
  customer: Customer;
}

const PaymentPreferenceView = ({
  onComplete,
  customer,
}: PaymentPreferenceProps) => {
  const [selectedPreference, setPreference] = useState('Digital');
  const [isManualSelect, setManualSelect] = useState(false);
  const [paymentPreferences, setPaymentPreferences] = useState<
    PaymentPreference[]
  >([]);
  const [organizationSettings, setOrganizationSettings] = useState<
    OrganizationSetting[]
  >([]);

  const handleSubmit = useCallback(
    async (preference: string, preferences: PaymentPreference[]) => {
      const paymentPreferencePk = preferences?.find(
        (el) => el.Customer_Payment_Preference_Type === preference,
      )?.Customer_Payment_Preference_PK;

      try {
        await updatePaymentPreferences(
          customer?.Customer_PK!,
          paymentPreferencePk!,
        );
        onComplete(preference === 'Digital');
      } catch (error) {
        notification.error({
          message: 'Failed',
          description: 'Update Payment preference failed',
        });
      }
    },
    [customer?.Customer_PK, onComplete],
  );

  const initialFetch = useCallback(async () => {
    try {
      const paymentPreferenceRes = await getPaymentPreferences();
      setPaymentPreferences(paymentPreferenceRes);

      const organizationSettingsRes = await getOrganizationSettings(
        customer.Organization_PK,
        'Contact Bank Connect',
        'Payment Preference',
      );
      setOrganizationSettings(organizationSettingsRes);
      const checkOptionSetting = organizationSettingsRes.find(
        (el: any) => el.Settings_Name === 'Bank Connect - Show Check Option',
      );
      if (checkOptionSetting && checkOptionSetting.Settings_Numeric === 0) {
        handleSubmit('Digital', paymentPreferenceRes);
      } else {
        setManualSelect(true);
      }
    } catch (error) {}
  }, [customer.Organization_PK, handleSubmit]);

  useEffect(() => {
    initialFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (changedValue: string) => () => {
    setPreference(changedValue);
  };

  const headerText = useMemo(() => {
    let text = `Congratulations! You're now ready to setup your payment preference.
    Choose one of the options below`;
    const headerSetting = organizationSettings.find(
      (el) => el.Settings_Name === 'Bank Connect - Digital Check Header Text',
    );
    if (headerSetting) {
      text = headerSetting.Settings_String || '';
    }
    return text;
  }, [organizationSettings]);

  if (!isManualSelect) {
    return null;
  }

  return (
    <div className="payment-preference-section">
      <p>{headerText}</p>
      <table>
        <tbody>
          <tr className="recommended-row">
            <td>
              <div>Recommended</div>
            </td>
            <td />
          </tr>
          <tr className="payment-method-wrapper">
            <td>
              <div>
                <div>Digital</div>
                <p>(ACH/Wire)</p>
              </div>
            </td>
            <td>
              <div>
                <div>Check</div>
              </div>
            </td>
          </tr>
          <tr className="payment-feature-section">
            <td>
              <ul>
                <li>- Get paid fast (1-3 days).</li>
                <li>- The modern way to get paid for busy professionals.</li>
                <li>- Payment deposited directly into your bank account.</li>
                <li>- No special trips to the bank</li>
                <li>- No lost checks</li>
              </ul>
            </td>
            <td>
              <ul>
                <li>- Get paid in 7-14 days.</li>
                <li>- Physical checks are delivered via USPS.</li>
                <li>- Requires trip to a bank to complete the deposit.</li>
                <li>- Susceptiable to lost checks</li>
              </ul>
            </td>
          </tr>
          <tr className="payment-method-section">
            <td>
              <Radio
                checked={selectedPreference === 'Digital'}
                onChange={handleChange('Digital')}
              >
                Pay me digitally
              </Radio>
            </td>
            <td>
              <Radio
                checked={selectedPreference === 'Check'}
                onChange={handleChange('Check')}
              >
                Pay me by check
              </Radio>
            </td>
          </tr>
        </tbody>
      </table>
      <div className="checkbox-wrapper">
        <Button
          onClick={() => handleSubmit(selectedPreference, paymentPreferences)}
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

export default PaymentPreferenceView;
